<template>
  <div class="faq-section">
    <b-card style="max-width: 34rem;">
      <b-card-title class="dark-variant">FAQ'S</b-card-title>
      <b-card-text class="faq-lead">
        Answers to the questions we get asked most. Search below or browse by topic.
      </b-card-text>

      <div class="faq-info">
        <!-- Search -->
        <div class="faq-search">
          <span class="material-icons">search</span>
          <input
            type="text"
            v-model="search"
            placeholder="Search the FAQs..."
            aria-label="Search the FAQs"
          />
          <button
            type="button"
            class="faq-search-clear"
            v-if="search"
            @click="search = ''"
            aria-label="Clear search"
          >
            <span class="material-icons">close</span>
          </button>
        </div>

        <!-- Category pills -->
        <div class="faq-cats">
          <button
            type="button"
            class="faq-cat"
            :class="activeCategory === 'all' ? 'is-active' : ''"
            @click="setCategory('all')"
          >All</button>
          <button
            type="button"
            class="faq-cat"
            v-for="category in categories"
            :key="category"
            :class="activeCategory === category ? 'is-active' : ''"
            @click="setCategory(category)"
          >{{ category }}</button>
        </div>

        <!-- Accordion -->
        <div class="faq-list" v-if="filteredFaqs.length">
          <div
            class="faq-item"
            v-for="item in filteredFaqs"
            :key="item.id"
            :class="isOpen(item.id) ? 'is-open' : ''"
          >
            <button
              type="button"
              class="faq-q"
              :aria-expanded="isOpen(item.id) ? 'true' : 'false'"
              @click="toggle(item.id)"
            >
              <span class="faq-q-text">{{ item.question }}</span>
              <span class="material-icons faq-chevron">expand_more</span>
            </button>
            <div class="faq-a" v-show="isOpen(item.id)">
              <div class="faq-a-inner" v-html="item.answer"></div>
              <p class="faq-tag">{{ item.category }}</p>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div class="faq-empty" v-else>
          <span class="material-icons">help_outline</span>
          <p>No FAQ matches "{{ search }}".</p>
          <router-link :to="{ name: 'FeedBack' }">Ask us directly</router-link>
        </div>
      </div>

      <div class="faq-foot">
        <p class="light-variant">Still stuck? Our team is happy to help.</p>
        <div class="faq-foot-links">
          <a href="mailto:support@amejpay.com">support@amejpay.com</a>
          <span class="faq-sep">|</span>
          <router-link :to="{ name: 'Help' }">Contact details</router-link>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
/**
 * Question / answer content.
 * `answer` accepts light HTML (lists, <strong>) and is rendered with v-html.
 * The content is static and authored here, so there is no injection surface.
 */
const FAQS = [
  {
    id: 1,
    category: "Getting Started",
    question: "How do I download the Amejpay app on my smartphone?",
    answer:
      "It is very simple on both iOS and Android. Open the App Store or Play Store, search for <strong>Amejpay</strong> and install it."
  },
  {
    id: 2,
    category: "Getting Started",
    question: "How do I sign up and create an Amejpay account?",
    answer:
      "<ol type='a'>" +
      "<li>Open the Amejpay app or go to www.amejpay.com</li>" +
      "<li>Click Sign Up and fill in your details.</li>" +
      "<li>You can also sign up via phone number or email using a verification code.</li>" +
      "<li>Complete your details to create your account.</li>" +
      "<li>Wait for the email confirmation for approval.</li>" +
      "<li>Once approved you will receive a confirmation email along with your Amejpay account.</li>" +
      "</ol>"
  },
  {
    id: 3,
    category: "Getting Started",
    question: "Where can I use Amejpay? Is it available in my country?",
    answer:
      "You can use Amejpay as a payment gateway for your business, for money transfers, utility payments and wallet to wallet transfers. The app will show whether the service is available depending on your country."
  },
  {
    id: 4,
    category: "Getting Started",
    question: "How do I use Amejpay?",
    answer:
      "<ol type='a'>" +
      "<li>Open the Amejpay app or website.</li>" +
      "<li>Log in to your account.</li>" +
      "<li>Select the service you want to use.</li>" +
      "</ol>"
  },
  {
    id: 5,
    category: "Money In",
    question: "How do I load funds into my Amejpay account?",
    answer:
      "<strong>Loading funds is fast and easy.</strong>" +
      "<ol type='a'>" +
      "<li>Open an account in the Amejpay app or website.</li>" +
      "<li>Sign up and create your account.</li>" +
      "<li>Connect your bank account, or load money with a Visa, Mastercard or debit card.</li>" +
      "</ol>"
  },
  {
    id: 6,
    category: "Money In",
    question: "How do I load money using a card?",
    answer:
      "Open <strong>Load Money</strong> from the top menu, enter the amount you want to add, then enter your card details on the secure payment step. Your wallet balance is topped up as soon as the payment is confirmed."
  },
  {
    id: 7,
    category: "Money In",
    question: "How much can I load in one day? Is there a limit?",
    answer: "You can load up to <strong>$1000</strong> per day."
  },
  {
    id: 8,
    category: "Payments & Requests",
    question: "How can I transfer funds?",
    answer:
      "You can transfer directly wallet to wallet, or from your bank to your wallet - make sure your account is linked with your bank account first."
  },
  {
    id: 9,
    category: "Payments & Requests",
    question: "Do both parties need Amejpay to make a transfer?",
    answer: "Yes, both parties must have an Amejpay wallet to make a direct transfer."
  },
  {
    id: 10,
    category: "Payments & Requests",
    question: "How do I know if someone has requested funds from me?",
    answer:
      "You will get a notification on your device. You will also receive an email, and you can check the notification section inside your account at any time."
  },
  {
    id: 11,
    category: "Payments & Requests",
    question: "What happens after I send a payment?",
    answer:
      "The transaction appears immediately in <strong>Transactions</strong>, where you can open it to view the full details, download a PDF receipt or share it with the other party."
  },
  {
    id: 12,
    category: "Money Out",
    question: "How do I withdraw money to my bank account?",
    answer:
      "Add your bank account under <strong>My Banks</strong> first, then open <strong>Withdraw</strong>, choose the account, enter the amount and confirm. You can hold up to 5 bank accounts on your profile."
  },
  {
    id: 13,
    category: "Money Out",
    question: "Can I cancel a withdrawal request?",
    answer:
      "Yes. A withdrawal can be cancelled from the withdrawal list while it is still <strong>Pending</strong>. Once it has been processed it can no longer be cancelled."
  },
  {
    id: 14,
    category: "Security & Verification",
    question: "What is OTP and how do I generate one?",
    answer:
      "A one time password is valid for a single login session or a single transaction, and is sent by email or SMS. It is generated for you automatically - just provide the email or phone number you want it sent to."
  },
  {
    id: 15,
    category: "Security & Verification",
    question: "Why didn't I receive my OTP?",
    answer:
      "The most common cause is a wrong email address or phone number on your profile. There may occasionally also be a technical delay - wait a moment and use the resend option."
  },
  {
    id: 16,
    category: "Security & Verification",
    question: "Why do I need to complete KYC?",
    answer:
      "KYC verifies your identity so we can raise your transaction limits and keep your wallet safe. Go to <strong>Settings &rarr; KYC</strong>, fill in your details and upload your document and selfie. Verification usually completes within a few working days."
  },
  {
    id: 17,
    category: "Security & Verification",
    question: "What is an MPIN and do I need one?",
    answer:
      "Your MPIN is a 6 digit code that protects sensitive actions in your wallet. Set or change it under <strong>Settings &rarr; MPIN</strong>. Avoid repeated digits and simple sequences - they are rejected for your safety."
  },
  {
    id: 18,
    category: "Fees & Rewards",
    question: "Can Amejpay be used as a savings account?",
    answer:
      "Yes. Amejpay is your digital bank where you can save and earn interest as well."
  },
  {
    id: 19,
    category: "Fees & Rewards",
    question: "How much interest can I get?",
    answer:
      "For a minimum saving of $100 held for a year or more you can get 10% annual interest."
  },
  {
    id: 20,
    category: "Fees & Rewards",
    question: "Is there any earning opportunity besides interest?",
    answer:
      "Yes. You can earn Amej Coin by referring your friends, and later convert Amej Coin into cash."
  },
  {
    id: 21,
    category: "Fees & Rewards",
    question: "Is there a transaction fee?",
    answer:
      "Yes. The applicable fee is always shown to you on the confirmation step before you approve a transaction."
  }
];

function plainText(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s{2,}/g, " ");
}

export default {
  name: "FAQSection",
  data() {
    return {
      faqs: FAQS,
      search: "",
      activeCategory: "all",
      openIds: []
    };
  },
  computed: {
    categories() {
      const seen = [];
      this.faqs.forEach(function (item) {
        if (seen.indexOf(item.category) === -1) { seen.push(item.category); }
      });
      return seen;
    },
    filteredFaqs() {
      const term = this.search.trim().toLowerCase();
      const category = this.activeCategory;
      return this.faqs.filter(function (item) {
        if (category !== "all" && item.category !== category) { return false; }
        if (!term) { return true; }
        const haystack = (
          item.question + " " + plainText(item.answer) + " " + item.category
        ).toLowerCase();
        return haystack.indexOf(term) !== -1;
      });
    }
  },
  watch: {
    // Auto-open the single match so a search lands straight on the answer.
    filteredFaqs(list) {
      if (this.search.trim() && list.length === 1) {
        this.openIds = [list[0].id];
      }
    }
  },
  methods: {
    isOpen(id) {
      return this.openIds.indexOf(id) !== -1;
    },
    toggle(id) {
      const index = this.openIds.indexOf(id);
      if (index === -1) {
        this.openIds.push(id);
      } else {
        this.openIds.splice(index, 1);
      }
    },
    setCategory(category) {
      this.activeCategory = category;
      this.openIds = [];
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../css/style.scss";

.faq-section {
  .faq-lead {
    font-size: 14px;
  }

  .faq-info {
    display: block;
  }

  .faq-search {
    position: relative;
    display: flex;
    align-items: center;
    background: #f7f9fc;
    border: 1px solid #e4e9f0;
    border-radius: 8px;
    padding: 0 10px;
    margin-bottom: 12px;

    .material-icons {
      font-size: 18px;
      color: #8a94a6;
      flex: 0 0 auto;
    }

    input {
      border: 0;
      background: transparent;
      outline: none;
      box-shadow: none;
      flex: 1 1 auto;
      width: 100%;
      min-width: 0;
      padding: 9px 8px;
      font-size: 14px;
    }
  }

  .faq-search-clear {
    border: 0;
    background: transparent;
    padding: 0;
    line-height: 1;
    cursor: pointer;

    .material-icons {
      font-size: 16px;
    }
  }

  .faq-cats {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 14px;
  }

  .faq-cat {
    border: 1px solid #e4e9f0;
    background: #fff;
    color: #6b7280;
    border-radius: 14px;
    font-size: 12px;
    padding: 3px 11px;
    margin: 0 6px 6px 0;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #0066b3;
      color: #0066b3;
    }

    &.is-active {
      background: #0066b3;
      border-color: #0066b3;
      color: #fff;
    }
  }

  .faq-list {
    border-top: 1px solid #f0f2f5;
  }

  .faq-item {
    border-bottom: 1px solid #f0f2f5;
  }

  .faq-q {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    text-align: left;
    background: transparent;
    border: 0;
    padding: 13px 2px;
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
    cursor: pointer;

    &:hover {
      color: #0066b3;
    }
  }

  .faq-q-text {
    flex: 1 1 auto;
    padding-right: 12px;
    line-height: 1.4;
  }

  .faq-chevron {
    flex: 0 0 auto;
    font-size: 20px;
    color: #8a94a6;
    transition: transform 0.2s ease;
  }

  .faq-item.is-open {
    .faq-q {
      color: #0066b3;
    }

    .faq-chevron {
      transform: rotate(180deg);
      color: #0066b3;
    }
  }

  .faq-a {
    padding: 0 2px 14px;
  }

  .faq-a-inner {
    font-size: 13px;
    line-height: 1.65;
    color: #6b7280;

    ol,
    ul {
      margin: 8px 0 0 18px;
      padding: 0;
    }

    li {
      margin-bottom: 5px;
    }
  }

  .faq-tag {
    display: inline-block;
    background: #eef4fb;
    color: #0066b3;
    border-radius: 10px;
    font-size: 11px;
    padding: 1px 9px;
    margin: 10px 0 0;
  }

  .faq-empty {
    text-align: center;
    padding: 28px 10px;
    color: #8a94a6;

    .material-icons {
      font-size: 34px;
      margin-bottom: 8px;
    }

    p {
      font-size: 14px;
      margin-bottom: 6px;
    }
  }

  .faq-foot {
    padding-top: 14px;
    margin-top: 6px;
    border-top: 1px solid #f0f2f5;

    p {
      font-size: 12px;
      margin-bottom: 6px;
    }
  }

  .faq-foot-links {
    font-size: 13px;

    .faq-sep {
      color: #cbd2dc;
      margin: 0 8px;
    }
  }
}
</style>
